import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { createErrorBanner } from 'components/Banner';
import { Dropdown, DropdownMenu, DropdownItem } from 'components/Dropdown2';
import Empty from 'components/Empty';
import FeatureNotSupported from 'components/FeatureNotSupported';
import { FlyoutContext, FlyoutProvider } from 'components/Flyout/FlyoutContext';
import Message from 'components/Message';
import { openModal, closeModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import PageTurner from 'components/PageTurner/PageTurner';
import { Table } from 'components/Table';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import { ICONS, NAV_ICONS } from 'constants/icons';
import { Title } from 'features/App';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { bem } from 'helpers/class-name';
import { reportError } from 'helpers/error-logging';
import { IA_KEY } from 'helpers/ia-logic';
import { formatEmailSubjectDateTime } from 'helpers/intl-fns';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';
import { selectIsDesktop } from 'selectors/ui';

import { SKU_ANALYTICS_EVENTS, SKU_ORDER_ACTION_SURFACE } from '../Items/SkuAnalytics.constants';
import { ITEM_AVAILABILITY_STATUS } from '../Items/SkuItems.constants';
import { fetchSkuCatalogItem, updateSkuItemAvailability } from '../Items/SkuItems.helpers';
import { getItemAvailabilityStatus } from '../Items/SkuMenuData.normalizers';
import useSkuVenuePermissions from '../Items/useSkuVenuePermissions';
import { skuMenuKeys } from '../Items/skuMenuKeys';
import SkuOrderFlyout, { SKU_ORDER_FLYOUT_ID } from '../shared/SkuOrderFlyout';
import { isCancelledOrderItem } from '../shared/SkuOrderFlyout.helpers';
import SkuSettingsGearButton from '../shared/SkuSettingsGearButton';
import SkuVenueOpenStatus from '../shared/SkuVenueOpenStatus';

import { buildCancelMessageHtml, mapOrderToTableRow } from './SkuOrderDashboard.helpers';
import messages from './SkuOrderDashboard.messages';
import SkuOrderLifecycleStatusFilter from './SkuOrderLifecycleStatusFilter';
import { SKU_AVAILABILITY_MODAL_NAME } from './SkuAvailabilityModal';
import { SKU_MISSED_PICKUP_MODAL_NAME } from './SkuMissedPickupModal';
import { SKU_OUT_OF_STOCK_CONFIRM_MODAL_NAME } from './SkuOutOfStockConfirmModal';
import './SkuOrderDashboard.scss';
import OrderStatusCell from './SkuOrderStatusCell';
import { OrderTableHeader, MobileOrderTableHeader, CellValue } from './SkuOrderTable';
import { useNewOrderNotifications } from './SkuOrderNotifications.hooks';
import {
  ORDER_ITEM_CANCELLATION_TYPE,
  PARTNER_ORDERS_REFETCH_INTERVAL_MS,
  PARTNER_ORDERS_TAB,
} from './SkuOrders.constants';
import {
  CANCEL_ORDER_CONFLICT,
  getCancelOrderConflict,
  useApplyOptimisticOrderCancelled,
  useCancelSkuOrder,
  useFulfillSkuOrder,
  useInvalidatePartnerOrders,
  usePartnerOrders,
} from './SkuOrders.hooks';

const { block, element } = bem('sku-orders-dashboard');

const ItemsCell = ({ groups }) => {
  if (!groups?.length) return <CellValue value={null} />;

  return (
    <span className={element('items-cell')}>
      {groups.map((group, idx) => (
        <React.Fragment key={group.groupKey}>
          {idx > 0 && ', '}
          <span className={element('items-cell-name', { 'crossed-out': group.isCrossedOut })}>
            {group.count > 1 ? `${group.name} (x${group.count})` : group.name}
          </span>
        </React.Fragment>
      ))}
    </span>
  );
};

ItemsCell.propTypes = {
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      groupKey: PropTypes.string.isRequired,
      itemId: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
      isCrossedOut: PropTypes.bool.isRequired,
    })
  ),
};

const TAB_TODAY = PARTNER_ORDERS_TAB.TODAY;
const TAB_HISTORY = PARTNER_ORDERS_TAB.HISTORY;

const CANCEL_ORDER_CONFLICT_MESSAGES = {
  [CANCEL_ORDER_CONFLICT.ALREADY_CANCELLED]: messages.cancelOrderAlreadyCancelled,
  [CANCEL_ORDER_CONFLICT.ORDER_CHANGED]: messages.cancelOrderChanged,
};

const SkuOrderDashboardContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const activeVenue = useSelector(selectActiveVenue);
  const { venueId, timezone: venueTimezone, venueAliasUrl, shareUrlConsumerVenue, venueName } = activeVenue;
  const { canEditItem, integrationName, source } = useSkuVenuePermissions();
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_ORDERS, venueId));
  const isDesktop = useSelector(selectIsDesktop);
  const [activeTab, setActiveTab] = useState(TAB_TODAY);
  const [resolvingOutOfStockOrderId, setResolvingOutOfStockOrderId] = useState(null);
  const resolvingOutOfStockRef = useRef(false);
  const [historyLifecycleStatus, setHistoryLifecycleStatus] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [fulfillingOrderId, setFulfillingOrderId] = useState(null);

  const { openFlyout } = useContext(FlyoutContext);
  const invalidatePartnerOrders = useInvalidatePartnerOrders();
  const applyOptimisticOrderCancelled = useApplyOptimisticOrderCancelled();

  const {
    orders,
    isLoading,
    isError,
    page,
    changePage,
    nextPageCursor,
    showPagination,
    startNumber,
    endNumber,
    dataUpdatedAt,
  } = usePartnerOrders(activeTab, {
    lifecycleStatus: activeTab === TAB_HISTORY ? historyLifecycleStatus : null,
    refetchInterval: PARTNER_ORDERS_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
    onError: () => {
      dispatch(createErrorBanner(intl.formatMessage(messages.loadOrdersError)));
    },
  });

  useNewOrderNotifications({
    orders,
    dataUpdatedAt,
    detectionKey: [venueId, activeTab, historyLifecycleStatus ?? 'all', page].join('|'),
    enabled: featureIsSupported && activeTab === TAB_TODAY && page === 1,
  });

  const { mutate: cancelOrder } = useCancelSkuOrder();

  const { mutate: fulfillOrder } = useFulfillSkuOrder({
    onSuccess: (_data, orderId) => {
      Segment.event(SKU_ANALYTICS_EVENTS.ORDER_MARKED_AS_FULFILLED, { venueId, source, orderId });
      invalidatePartnerOrders();
      setFulfillingOrderId(null);
    },
    onError: () => {
      dispatch(createErrorBanner(intl.formatMessage(messages.fulfillOrderError)));
      setFulfillingOrderId(null);
    },
  });

  useEffect(() => {
    if (!featureIsSupported || !venueId) return;
    Segment.event(SKU_ANALYTICS_EVENTS.ORDER_MANAGEMENT_PAGE_OPENED, { venueId, source });
  }, [featureIsSupported, venueId, source]);

  useEffect(() => {
    if (!featureIsSupported || !venueId || activeTab !== TAB_HISTORY) return;
    Segment.event(SKU_ANALYTICS_EVENTS.ORDER_HISTORY_VIEWED, { venueId, source });
  }, [activeTab, featureIsSupported, venueId, source]);

  const handleFulfill = useCallback(
    orderId => {
      setFulfillingOrderId(orderId);
      fulfillOrder(orderId);
    },
    [fulfillOrder]
  );

  const handleCancelOrderError = useCallback(
    error => {
      const conflict = getCancelOrderConflict(error);
      if (conflict) {
        dispatch(createErrorSnackbar(CANCEL_ORDER_CONFLICT_MESSAGES[conflict]));
        invalidatePartnerOrders();
        return;
      }
      dispatch(createErrorSnackbar(messages.cancelOrderError, {}, {}, getApiErrorSnackbarDescription(error)));
    },
    [dispatch, invalidatePartnerOrders]
  );

  const notifyCancelAccepted = useCallback(
    (data, successMessage) => {
      dispatch(
        data?.menuUpdateFailed ? createErrorSnackbar(messages.menuUpdateFailed) : createSuccessSnackbar(successMessage)
      );
    },
    [dispatch]
  );

  const openCancelModal = useCallback(
    (orderId, cancellationType) => {
      const order = orders.find(o => o?.core?.orderId === orderId) || null;
      if (!order) return;

      const orderCreatedOn = order?.core?.createdOn;
      const activeVenueName = venueName || '';
      const userName = order?.userName || '';

      const subjectValues = orderCreatedOn
        ? { ...formatEmailSubjectDateTime(orderCreatedOn, venueTimezone, intl), venueName: activeVenueName }
        : { time: '', date: '', venueName: activeVenueName };

      const subject = intl.formatMessage(messages.cancelOrderSubject, { venueName: activeVenueName });
      const messageHtml = buildCancelMessageHtml(intl, {
        time: subjectValues.time,
        date: subjectValues.date,
        venueName: activeVenueName,
        userName,
        venueAliasUrl,
        shareUrlConsumerVenue,
        creditAmountTotal: order?.creditAmountTotal,
        cancellationType,
      });

      dispatch(
        openModal('SkuCancelConfirmModal', {
          hasCloseButton: true,
          onRequestClose: () => dispatch(closeModal()),
          onSubmit: (_values, { resolveSubmit, rejectSubmit }) => {
            cancelOrder(
              { orderId, cancellationType },
              {
                onSuccess: data => {
                  Segment.event(SKU_ANALYTICS_EVENTS.ORDER_CANCELLED, {
                    venueId,
                    source,
                    orderId,
                    cancellationType,
                  });
                  resolveSubmit();
                  dispatch(closeModal());
                  applyOptimisticOrderCancelled(orderId, cancellationType);
                  notifyCancelAccepted(data, messages.cancelOrderSuccessWithNotification);
                },
                onError: error => {
                  rejectSubmit();
                  dispatch(closeModal());
                  handleCancelOrderError(error);
                },
              }
            );
          },
          title: <Message message={messages.cancelOrderModalTitle} />,
          to: userName,
          subject,
          initialValues: { message: messageHtml },
        })
      );
    },
    [
      dispatch,
      intl,
      venueTimezone,
      venueAliasUrl,
      shareUrlConsumerVenue,
      venueName,
      orders,
      cancelOrder,
      applyOptimisticOrderCancelled,
      notifyCancelAccepted,
      handleCancelOrderError,
      venueId,
      source,
    ]
  );

  const handleCancelOrder = useCallback(
    orderId => openCancelModal(orderId, ORDER_ITEM_CANCELLATION_TYPE.PARTNER_CANCELLED),
    [openCancelModal]
  );

  const handleCustomerMissedPickup = useCallback(
    (orderId, { surface } = {}) => {
      dispatch(
        openModal(SKU_MISSED_PICKUP_MODAL_NAME, {
          onCancel: () => dispatch(closeModal()),
          onConfirm: () => {
            const cancellationType = ORDER_ITEM_CANCELLATION_TYPE.USER_MISSED_PICKUP;
            dispatch(closeModal());
            cancelOrder(
              { orderId, cancellationType },
              {
                onSuccess: data => {
                  Segment.event(SKU_ANALYTICS_EVENTS.USER_MARKED_AS_MISSED_PICKUP, {
                    venueId,
                    source,
                    orderId,
                    surface,
                  });
                  Segment.event(SKU_ANALYTICS_EVENTS.ORDER_CANCELLED, {
                    venueId,
                    source,
                    orderId,
                    cancellationType,
                  });
                  applyOptimisticOrderCancelled(orderId, cancellationType);
                  notifyCancelAccepted(data, messages.cancelOrderSuccess);
                },
                onError: handleCancelOrderError,
              }
            );
          },
        })
      );
    },
    [
      dispatch,
      cancelOrder,
      applyOptimisticOrderCancelled,
      notifyCancelAccepted,
      handleCancelOrderError,
      venueId,
      source,
    ]
  );

  const openEditItemAvailability = useCallback(
    (orderId, { itemId, itemName, orderItemIds }) => {
      if (resolvingOutOfStockRef.current || !itemId || !orderItemIds?.length) return;

      const order = orders.find(o => o?.core?.orderId === orderId);
      if (!order) return;
      const isOnlyItemInOrder = order.orderItems
        .filter(item => !isCancelledOrderItem(item))
        .every(item => orderItemIds.includes(item.core?.orderItemId));

      const partialCancelSuccessMessage = canEditItem ? messages.itemMarkedOutOfStock : messages.itemRemovedFromOrder;

      let prefetchedCatalogItem = null;

      const cancelOutOfStockItems = selectedStatus => {
        const cancellationType = ORDER_ITEM_CANCELLATION_TYPE.PARTNER_SOLD_OUT;
        const priorStatus = getItemAvailabilityStatus(prefetchedCatalogItem);
        const releaseItemMenu = () => {
          resolvingOutOfStockRef.current = false;
          setResolvingOutOfStockOrderId(null);
        };

        resolvingOutOfStockRef.current = true;
        setResolvingOutOfStockOrderId(orderId);
        cancelOrder(
          { orderId, cancellationType, orderItemIds },
          {
            onSuccess: async data => {
              releaseItemMenu();
              if (isOnlyItemInOrder) {
                Segment.event(SKU_ANALYTICS_EVENTS.ORDER_CANCELLED, { venueId, source, orderId, cancellationType });
              }
              Segment.event(SKU_ANALYTICS_EVENTS.ITEM_MARKED_OUT_OF_STOCK, {
                venueId,
                source,
                orderId,
                itemId,
                orderItemIds,
                ...(selectedStatus && { from: priorStatus, to: selectedStatus }),
              });
              applyOptimisticOrderCancelled(orderId, cancellationType, orderItemIds);

              // The backend already flips the item sold out until venue midnight; the menu only needs a
              // write to enter or leave the indefinite state.
              const needsMenuWrite =
                priorStatus !== selectedStatus &&
                [priorStatus, selectedStatus].includes(ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY);
              let menuUpdateFailed = Boolean(data?.menuUpdateFailed);
              if (needsMenuWrite) {
                try {
                  await updateSkuItemAvailability(venueId, itemId, selectedStatus, {
                    queryClient,
                    catalogItem: prefetchedCatalogItem,
                    nameFallback: itemName,
                    timezone: venueTimezone,
                  });
                  Segment.event(SKU_ANALYTICS_EVENTS.ITEM_AVAILABILITY_CHANGED, {
                    venueId,
                    itemId,
                    from: priorStatus,
                    to: selectedStatus,
                    source,
                  });
                  menuUpdateFailed = false;
                } catch {
                  menuUpdateFailed = true;
                }
              }
              notifyCancelAccepted(
                { menuUpdateFailed },
                isOnlyItemInOrder ? messages.cancelOrderSuccessWithNotification : partialCancelSuccessMessage
              );
              queryClient.invalidateQueries({ queryKey: skuMenuKeys.all });
            },
            onError: error => {
              releaseItemMenu();
              handleCancelOrderError(error);
            },
          }
        );
      };

      function openAvailabilityModal(initialStatus) {
        dispatch(
          openModal(SKU_AVAILABILITY_MODAL_NAME, {
            itemName,
            isOnlyItemInOrder,
            initialStatus,
            onConfirm: selectedStatus => {
              dispatch(closeModal());
              if (!isOnlyItemInOrder) {
                cancelOutOfStockItems(selectedStatus);
                return;
              }
              dispatch(
                openModal(SKU_OUT_OF_STOCK_CONFIRM_MODAL_NAME, {
                  onRequestClose: () => dispatch(closeModal()),
                  onCancel: () => {
                    dispatch(closeModal());
                    openAvailabilityModal(selectedStatus);
                  },
                  onConfirm: () => {
                    dispatch(closeModal());
                    cancelOutOfStockItems(selectedStatus);
                  },
                })
              );
            },
            onCancel: () => dispatch(closeModal()),
          })
        );
      }

      async function resolveStatusAndOpen() {
        resolvingOutOfStockRef.current = true;
        setResolvingOutOfStockOrderId(orderId);
        try {
          prefetchedCatalogItem = await fetchSkuCatalogItem(venueId, itemId, { queryClient });
        } catch (error) {
          reportError('Could not resolve SKU item availability before the sold-out modal', {
            error,
            venueId,
            itemId,
          });
        } finally {
          resolvingOutOfStockRef.current = false;
          setResolvingOutOfStockOrderId(null);
        }
        const isUnavailableIndefinitely =
          getItemAvailabilityStatus(prefetchedCatalogItem) === ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY;
        openAvailabilityModal(
          isUnavailableIndefinitely
            ? ITEM_AVAILABILITY_STATUS.UNAVAILABLE_INDEFINITELY
            : ITEM_AVAILABILITY_STATUS.UNAVAILABLE_TODAY
        );
      }

      // Integrated venues manage availability in their POS, so they only confirm the cancellation.
      if (!canEditItem) {
        dispatch(
          openModal(SKU_OUT_OF_STOCK_CONFIRM_MODAL_NAME, {
            removedItemName: itemName,
            isOnlyItemInOrder,
            integrationName,
            onRequestClose: () => dispatch(closeModal()),
            onCancel: () => dispatch(closeModal()),
            onConfirm: () => {
              dispatch(closeModal());
              cancelOutOfStockItems(null);
            },
          })
        );
        return;
      }

      resolveStatusAndOpen();
    },
    [
      canEditItem,
      integrationName,
      dispatch,
      orders,
      cancelOrder,
      applyOptimisticOrderCancelled,
      notifyCancelAccepted,
      handleCancelOrderError,
      queryClient,
      venueId,
      source,
      venueTimezone,
    ]
  );

  const ordersForTab = useMemo(
    () =>
      orders.map(order =>
        mapOrderToTableRow(order, {
          intl,
          venueTimezone,
          locale: intl.locale,
        })
      ),
    [orders, intl, venueTimezone]
  );

  const handleRowClick = useCallback(
    orderId => {
      setSelectedOrderId(orderId);
      openFlyout(SKU_ORDER_FLYOUT_ID);
      Segment.event(SKU_ANALYTICS_EVENTS.ORDER_FLYOUT_OPENED, { venueId, source, orderId });
    },
    [openFlyout, venueId, source]
  );

  const selectedOrder = useMemo(
    () => orders.find(o => o?.core?.orderId === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  if (!featureIsSupported) {
    return (
      <>
        <Message message={messages.pageTitle} as={Title} />
        <FeatureNotSupported />
      </>
    );
  }

  const showEmpty = !isLoading && !isError && ordersForTab.length === 0;

  return (
    <>
      <Message message={messages.pageTitle} as={Title} />

      <div className={block()}>
        <div className={element('tabs-header')}>
          <div className={element('tab-controls')}>
            <SkuVenueOpenStatus />
            <Dropdown
              attachment="right"
              className={element('settings-dropdown-wrapper')}
              contentClassName={element('settings-dropdown-content')}>
              {({ toggleMenu, closeMenu }) => (
                <>
                  <SkuSettingsGearButton ariaLabel={intl.formatMessage(messages.settingsButton)} onClick={toggleMenu} />
                  <DropdownMenu className={element('settings-dropdown')}>
                    <DropdownItem
                      onClick={() => {
                        Segment.event('Click Manage Items', { venueId, type: 'venue' });
                        navigate(makePath.items(venueId));
                        closeMenu();
                      }}>
                      <span className={element('settings-menu-item')}>
                        <Isvg src={ICONS.editPencilSku} className={element('settings-menu-icon')} />
                        <Message message={messages.manageItems} />
                      </span>
                    </DropdownItem>
                    <DropdownItem
                      onClick={() => {
                        Segment.event('Click Business Profile', { venueId, type: 'venue' });
                        navigate(makePath.businessProfile(venueId));
                        closeMenu();
                      }}>
                      <span className={element('settings-menu-item')}>
                        <Isvg src={ICONS.storefront} className={element('settings-menu-icon')} />
                        <Message message={messages.businessProfile} />
                      </span>
                    </DropdownItem>
                  </DropdownMenu>
                </>
              )}
            </Dropdown>
          </div>
          <Tabs selectedTab={activeTab} onTabChange={setActiveTab}>
            <Tab name={TAB_TODAY}>
              <TabLabel>
                <Message message={messages.tabToday} />
              </TabLabel>
            </Tab>
            <Tab name={TAB_HISTORY}>
              <TabLabel>
                <Message message={messages.tabHistory} />
              </TabLabel>
            </Tab>
          </Tabs>
        </div>

        {activeTab === TAB_HISTORY && (
          <SkuOrderLifecycleStatusFilter value={historyLifecycleStatus} onChange={setHistoryLifecycleStatus} />
        )}

        {showEmpty ? (
          <Empty
            icon={NAV_ICONS.appointments}
            title={messages.emptyOrders}
            subtitle={{ ...messages.emptyOrdersSubtitle, values: { br: <br /> } }}
          />
        ) : (
          <>
            <Table
              header={isDesktop ? <OrderTableHeader /> : <MobileOrderTableHeader />}
              emptyMessage={messages.emptyOrders}
              isLoading={isLoading}>
              {ordersForTab.map(order =>
                isDesktop ? (
                  <tr
                    key={order.id}
                    className={element('order-row')}
                    onClick={() => handleRowClick(order.rawOrderId)}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                    }}>
                    <td>
                      <CellValue value={order.memberName} />
                    </td>
                    <td>
                      <CellValue value={order.id} />
                    </td>
                    <td>
                      <ItemsCell groups={order.itemGroups} />
                    </td>
                    <td>
                      <CellValue value={order.payableAmountDisplay} align="right" />
                    </td>
                    <td>
                      <CellValue value={order.pickupDate} align="right" />
                    </td>
                    <td>
                      <CellValue value={order.pickupTime} align="right" />
                    </td>
                    <td>
                      <OrderStatusCell
                        lifecycleStatus={order.lifecycleStatus}
                        showRowActions={isDesktop}
                        onFulfill={() => handleFulfill(order.rawOrderId)}
                        isFulfilling={fulfillingOrderId === order.rawOrderId}
                        onEditItem={() => handleRowClick(order.rawOrderId)}
                        onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                        onCustomerMissedPickup={() =>
                          handleCustomerMissedPickup(order.rawOrderId, {
                            surface: SKU_ORDER_ACTION_SURFACE.ACTIVE_ORDER_MENU,
                          })
                        }
                        orderId={order.rawOrderId}
                        userName={order.memberName}
                        userId={order.userId}
                        venueName={venueName}
                        orderCreatedOn={order.createdOn}
                        isInTodayTab={activeTab === TAB_TODAY}
                      />
                    </td>
                  </tr>
                ) : (
                  <tr
                    key={order.id}
                    className={element('order-row')}
                    onClick={() => handleRowClick(order.rawOrderId)}
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') handleRowClick(order.rawOrderId);
                    }}>
                    <td>
                      <CellValue value={order.memberName} />
                    </td>
                    <td>
                      <OrderStatusCell
                        lifecycleStatus={order.lifecycleStatus}
                        showRowActions={isDesktop}
                        onFulfill={() => handleFulfill(order.rawOrderId)}
                        isFulfilling={fulfillingOrderId === order.rawOrderId}
                        onEditItem={() => handleRowClick(order.rawOrderId)}
                        onCancelOrder={() => handleCancelOrder(order.rawOrderId)}
                        onCustomerMissedPickup={() =>
                          handleCustomerMissedPickup(order.rawOrderId, {
                            surface: SKU_ORDER_ACTION_SURFACE.ACTIVE_ORDER_MENU,
                          })
                        }
                        orderId={order.rawOrderId}
                        userName={order.memberName}
                        userId={order.userId}
                        venueName={venueName}
                        orderCreatedOn={order.createdOn}
                        isInTodayTab={activeTab === TAB_TODAY}
                      />
                    </td>
                  </tr>
                )
              )}
            </Table>
            {showPagination && (
              <PageTurner
                className="sku-orders-page-turner"
                endNumber={endNumber}
                isLoading={isLoading}
                isOnFirstPage={page === 1}
                isOnLastPage={!nextPageCursor}
                startNumber={startNumber}
                viewNextPage={() => changePage(page + 1)}
                viewPrevPage={() => changePage(page - 1)}
              />
            )}
          </>
        )}
      </div>

      <SkuOrderFlyout
        order={selectedOrder ?? undefined}
        onFulfill={selectedOrder?.core?.orderId ? () => handleFulfill(selectedOrder.core.orderId) : undefined}
        isFulfilling={fulfillingOrderId === selectedOrder?.core?.orderId}
        onCancelOrder={selectedOrder?.core?.orderId ? () => handleCancelOrder(selectedOrder.core.orderId) : undefined}
        onEditItemAvailability={
          selectedOrder?.core?.orderId
            ? payload => openEditItemAvailability(selectedOrder.core.orderId, payload)
            : undefined
        }
        isResolvingOutOfStock={resolvingOutOfStockOrderId === selectedOrder?.core?.orderId}
        onCustomerMissedPickup={
          selectedOrder?.core?.orderId
            ? () =>
                handleCustomerMissedPickup(selectedOrder.core.orderId, {
                  surface: SKU_ORDER_ACTION_SURFACE.ORDER_FLYOUT,
                })
            : undefined
        }
        isInTodayTab={activeTab === TAB_TODAY}
      />
    </>
  );
};

const SkuOrderDashboard = () => (
  <FlyoutProvider>
    <SkuOrderDashboardContent />
  </FlyoutProvider>
);

export default SkuOrderDashboard;
